import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Popconfirm, Progress, Select, Space, Switch, Table, Tabs, Tag, Typography, message } from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined, TranslationOutlined } from '@ant-design/icons';
import { PermissionControl, usePermissions } from '../../permissions/access';
import { showRequestError } from '../../utils/request-feedback';
import {
    fetchRecommendationQuestions, saveRecommendationQuestion, deleteRecommendationQuestion,
    fetchRecommendationTags, renameRecommendationTag, startRecommendationTranslation, fetchRecommendationTranslationJob,
} from '../../api/recommendations';
import RecommendationTranslations, { translationLanguages } from './recommendation_translations';

const initialQuestion = { prompt: '', tags: [], sortOrder: 0, enabled: true };
const required = [{ required: true, whitespace: true, message: '请填写此项' }];

export function RecommendationPanel() {
    const [form] = Form.useForm();
    const [tagForm] = Form.useForm();
    const { can } = usePermissions();
    const [query, setQuery] = useState({ page: 1, pageSize: 20, search: '', tag: '' });
    const [tags, setTags] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [revision, setRevision] = useState(0);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [renaming, setRenaming] = useState(null);
    const [savingTag, setSavingTag] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const requestVersion = useRef(0);
    const [translationTarget, setTranslationTarget] = useState(null);
    const [translationJob, setTranslationJob] = useState(null);
    const [startingTranslation, setStartingTranslation] = useState(false);
    const [translationScope, setTranslationScope] = useState(null);
    const [jobError, setJobError] = useState(false);
    const jobVersion = useRef(0);
    const jobAction = useRef(0);
    const lastJob = useRef(null);
    const refresh = () => setRevision(value => value + 1);

    useEffect(() => {
        const version = ++requestVersion.current;
        setLoading(true);
        setError(false);
        Promise.all([fetchRecommendationQuestions(query), fetchRecommendationTags()]).then(([result, tagResult]) => {
            if (version !== requestVersion.current) return;
            const nextTags = tagResult.items || [];
            setTags(nextTags);
            if (query.tag && !nextTags.some(tag => tag.name === query.tag)) {
                setQuery(value => ({ ...value, tag: '', page: 1 }));
                return;
            }
            const count = Number(result.total || 0);
            if (query.page > 1 && (query.page - 1) * query.pageSize >= count) {
                setQuery(value => ({ ...value, page: Math.max(1, Math.ceil(count / value.pageSize)) }));
                return;
            }
            setRows(result.items || []);
            setTotal(count);
        }).catch(err => {
            if (version !== requestVersion.current) return;
            setError(true);
            setRows([]);
            setTotal(0);
            showRequestError(err);
        }).finally(() => {
            if (version === requestVersion.current) setLoading(false);
        });
        return () => { requestVersion.current++; };
    }, [query, revision]);

    useEffect(() => {
        const version = ++jobVersion.current;
        let timer;
        const poll = async () => {
            const action = jobAction.current;
            let delay = 1500;
            try {
                const job = await fetchRecommendationTranslationJob();
                if (version !== jobVersion.current || action !== jobAction.current) return;
                setJobError(false);
                if (job.jobId && job.status !== 'running' && (lastJob.current?.status === 'running' || lastJob.current?.jobId !== job.jobId)) setRevision(value => value + 1);
                lastJob.current = job;
                setTranslationJob(job.jobId ? job : null);
                delay = job.status === 'running' ? 1500 : 5000;
            } catch (error) {
                if (version !== jobVersion.current || action !== jobAction.current) return;
                setJobError(true);
                delay = 5000;
            } finally {
                if (version === jobVersion.current) timer = setTimeout(poll, delay);
            }
        };
        poll();
        return () => { jobVersion.current++; clearTimeout(timer); };
    }, []);

    const translateAll = async () => {
        if (startingTranslation || translationJob?.status === 'running' || !translationScope || !can('airecommendations.translate')) return;
        setStartingTranslation(true);
        jobAction.current++;
        try {
            const job = await startRecommendationTranslation(translationScope);
            setTranslationScope(null);
            lastJob.current = job;
            setTranslationJob(job);
            setJobError(false);
            message.info(job.status === 'running' ? '翻译任务已开始，完成后自动保存' : '当前任务已结束，可刷新查看');
        } catch (error) { showRequestError(error); }
        finally { setStartingTranslation(false); }
    };
    const openTagTranslations = () => {
        const tag = tags.find(item => item.name === query.tag);
        if (tag) setTranslationTarget({ kind: 'tag', sourceText: tag.name, translations: tag.translations });
    };

    const edit = item => {
        form.resetFields();
        form.setFieldsValue({ ...initialQuestion, tags: query.tag ? [query.tag] : [], ...item,
            ...(item ? { enabled: Boolean(item.enabled) } : {}),
        });
        setEditing(item || {});
    };
    const save = async values => {
        if (saving || !can('airecommendations.saveQuestion')) return;
        setSaving(true);
        try {
            await saveRecommendationQuestion({ ...values, id: editing.id || '0',
                prompt: values.prompt.trim(), tags: [...new Set(values.tags.map(tag => tag.trim()).filter(Boolean))],
            });
            message.success('问题已保存');
            setEditing(null);
            refresh();
        } catch (err) {
            showRequestError(err);
        } finally {
            setSaving(false);
        }
    };
    const remove = async item => {
        setDeleting(item.id);
        try {
            await deleteRecommendationQuestion(item.id);
            message.success('已删除');
            refresh();
        } catch (err) {
            showRequestError(err);
        } finally {
            setDeleting(null);
        }
    };
    const editTag = () => {
        tagForm.resetFields();
        tagForm.setFieldsValue({ newTag: query.tag });
        setRenaming(query.tag);
    };
    const saveTag = async ({ newTag }) => {
        if (savingTag || !can('airecommendations.renameTag')) return;
        setSavingTag(true);
        try {
            const name = newTag.trim();
            await renameRecommendationTag({ oldTag: renaming, newTag: name });
            message.success('Tag 已更新');
            setRenaming(null);
            setQuery(value => ({ ...value, tag: name, page: 1 }));
            refresh();
        } catch (err) {
            showRequestError(err);
        } finally {
            setSavingTag(false);
        }
    };
    const columns = [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '英文问题', dataIndex: 'prompt', width: 360,
            render: text => <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{text}</div> },
        { title: 'Tag', dataIndex: 'tags', width: 140, render: values => (values || []).map(tag => <Tag key={tag}>{tag}</Tag>) },
        { title: '排序权重', dataIndex: 'sortOrder', width: 90, render: value => value || 0 },
        { title: '展示状态', dataIndex: 'enabled', width: 90,
            render: enabled => <Tag color={enabled ? 'green' : 'default'}>{enabled ? '已启用' : '已停用'}</Tag> },
        { title: '译文', width: 130, render: (_, row) => {
            const count = translationLanguages.filter(item => row.translations?.[item.key]).length;
            return <Tag color={count === 5 ? 'green' : 'orange'}>{count}/5 {count === 5 ? '已就绪' : '待翻译'}</Tag>;
        } },
        { title: '操作', width: 190, fixed: 'right', render: (_, row) => <Space>
            <PermissionControl permission="airecommendations.read"><Button type="link" onClick={() => setTranslationTarget({ kind: 'question', questionId: row.id, sourceText: row.prompt, translations: row.translations })}>译文</Button></PermissionControl>
            <PermissionControl permission="airecommendations.saveQuestion"><Button type="link" onClick={() => edit(row)}>编辑</Button></PermissionControl>
            <PermissionControl permission="airecommendations.deleteQuestion"><Popconfirm title="删除推荐问题？"
                description="将从所有所属 Tag 下删除此推荐问题，历史研究记录保留。"
                onConfirm={() => remove(row)} okText="删除" cancelText="取消" okButtonProps={{ danger: true }}>
                <Button danger type="link" loading={deleting === row.id} disabled={deleting !== null}>删除</Button>
            </Popconfirm></PermissionControl>
        </Space> },
    ];
    return <>
        <Typography.Paragraph type="secondary">先保存英文问题和英文 Tag，再分别翻译问题和 Tag。重新翻译会覆盖对应类型的五种语言译文，包括手动修改的内容；执行前需要确认。</Typography.Paragraph>
        <Tabs activeKey={query.tag} onChange={tag => setQuery(value => ({ ...value, tag, page: 1 }))}
            items={[{ key: '', label: '全部问题' }, ...tags.map(tag => ({ key: tag.name, label: `${tag.name} (${tag.questionCount || 0})` }))]} />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Space wrap>
                <PermissionControl permission="airecommendations.translate"><Button icon={<TranslationOutlined />} onClick={() => setTranslationScope('question')}
                    disabled={startingTranslation || translationJob?.status === 'running'}>翻译全部译文</Button></PermissionControl>
                <PermissionControl permission="airecommendations.translate"><Button icon={<TranslationOutlined />} onClick={() => setTranslationScope('tag')}
                    disabled={startingTranslation || translationJob?.status === 'running'}>翻译全部 Tag</Button></PermissionControl>
                <PermissionControl permission="airecommendations.saveQuestion"><Button type="primary" icon={<PlusOutlined />} onClick={() => edit(null)}>新增推荐问题</Button></PermissionControl>
                {query.tag && <PermissionControl permission="airecommendations.read"><Button onClick={openTagTranslations}>Tag 译文</Button></PermissionControl>}
                {query.tag && <PermissionControl permission="airecommendations.renameTag"><Button icon={<EditOutlined />} onClick={editTag} disabled={loading || error}>修改 Tag</Button></PermissionControl>}
            </Space>
            <Space wrap>
                <Input.Search aria-label="搜索推荐问题" placeholder={query.tag ? '搜索当前 Tag 下的问题' : '搜索推荐问题'}
                    allowClear maxLength={200} onSearch={search => setQuery(value => ({ ...value, search: search.trim(), page: 1 }))} style={{ width: 280 }} />
                <PermissionControl permission="airecommendations.read"><Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>刷新</Button></PermissionControl>
            </Space>
        </div>
        {jobError && <Alert type="warning" showIcon message="暂时无法读取翻译进度，正在自动重试；已保存的译文不受影响。" style={{ marginBottom: 12 }} />}
        {translationJob && <Alert type={translationJob.status === 'completed' ? 'success' : ['partial', 'failed'].includes(translationJob.status) ? 'warning' : 'info'} showIcon
            style={{ marginBottom: 16 }} message={({ running: '正在翻译并保存', completed: '本次翻译已完成', partial: '部分译文未完成，请检查后重试', failed: '翻译失败，请检查后重试' })[translationJob.status]}
            description={<><Progress percent={translationJob.total ? Math.round(100 * (Number(translationJob.completed || 0) + Number(translationJob.failed || 0) + Number(translationJob.skipped || 0)) / translationJob.total) : translationJob.status === 'completed' ? 100 : 0}
                status={translationJob.status === 'running' ? 'active' : translationJob.status === 'completed' ? 'success' : 'exception'} />
                已保存{translationJob.scope === 'question' ? '问题译文' : translationJob.scope === 'tag' ? 'Tag 译文' : '译文（含问题和 Tag）'} {translationJob.completed || 0} / {translationJob.total || 0}；失败 {translationJob.failed || 0}；原文变更跳过 {translationJob.skipped || 0}。{translationJob.overwrite ? '本任务按确认覆盖所选类型的五种语言译文，包括手动修订；另一类型的译文及英文原文不变。' : '本任务仅补齐缺失译文，已有译文及手动修订保留。'}</>} />}
        {error && <Alert type="error" showIcon message="加载失败，请点击刷新重试" style={{ marginBottom: 16 }} />}
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 1080 }}
            locale={{ emptyText: error ? '数据暂不可用' : '暂无推荐问题' }}
            pagination={{ current: query.page, pageSize: query.pageSize, total, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100],
                showTotal: count => `共 ${count} 条`, onChange: (page, pageSize) => setQuery(value => ({ ...value, page, pageSize })) }} />
        <PermissionControl permission="airecommendations.saveQuestion"><Modal title={`${editing?.id ? '编辑' : '新增'}推荐问题`} open={editing !== null} forceRender
            width={720} style={{ top: 24 }} styles={{ body: { maxHeight: 'calc(100vh - 190px)', overflowY: 'auto', paddingRight: 8 } }}
            onCancel={() => { if (!saving) setEditing(null); }} maskClosable={!saving} closable={!saving}
            keyboard={!saving} onOk={() => form.submit()} confirmLoading={saving} okText="保存" cancelText="取消"
            okButtonProps={{ disabled: !can('airecommendations.saveQuestion') }} cancelButtonProps={{ disabled: saving }}>
            <Form form={form} layout="vertical" onFinish={save} disabled={saving}>
                <Form.Item name="prompt" label="英文问题" rules={required}><Input.TextArea rows={5} maxLength={20000} showCount /></Form.Item>
                <Form.Item name="tags" label="所属英文 Tag" extra="输入新 Tag 后按 Enter。同一问题归属多个 Tag 时，共享问题内容。" rules={[{ validator: (_, values) => {
                    const cleaned = (values || []).map(tag => tag.trim()).filter(Boolean);
                    return cleaned.length > 0 && cleaned.length <= 20 && cleaned.every(tag => tag.length <= 100)
                        ? Promise.resolve() : Promise.reject(new Error('请填写 1–20 个 Tag，每个不超过 100 字'));
                } }]}><Select mode="tags" tokenSeparators={[',', '，']} options={tags.map(tag => ({ value: tag.name, label: tag.name }))} /></Form.Item>
                <Form.Item name="sortOrder" label="排序权重" extra="权重越大越靠前，同权重按新增时间倒序。" rules={[{ required: true }]}><InputNumber min={-2147483648} max={2147483647} precision={0} /></Form.Item>
                <Form.Item name="enabled" label="启用推荐" valuePropName="checked"><Switch /></Form.Item>
            </Form>
        </Modal></PermissionControl>
        <PermissionControl permission="airecommendations.renameTag"><Modal title="修改 Tag" open={renaming !== null} forceRender
            onCancel={() => { if (!savingTag) setRenaming(null); }} onOk={() => tagForm.submit()} confirmLoading={savingTag}
            okText="保存 Tag" cancelText="取消" maskClosable={!savingTag} closable={!savingTag} keyboard={!savingTag}
            okButtonProps={{ disabled: !can('airecommendations.renameTag') }} cancelButtonProps={{ disabled: savingTag }}>
            <Typography.Paragraph>将更新「{renaming}」下所有问题的 Tag，包括其他分页和已停用的问题。</Typography.Paragraph>
            <Form form={tagForm} layout="vertical" onFinish={saveTag} disabled={savingTag}>
                <Form.Item name="newTag" label="英文 Tag 名称" rules={[...required, { validator: (_, value) => {
                    const name = (value || '').trim();
                    return name === renaming || !tags.some(tag => tag.name === name)
                        ? Promise.resolve() : Promise.reject(new Error('该 Tag 已存在，请使用其他名称'));
                } }]}><Input maxLength={100} /></Form.Item>
            </Form>
        </Modal></PermissionControl>
        <PermissionControl permission="airecommendations.translate"><Modal className="recommendation-translation-confirm" title={translationScope === 'tag' ? '翻译全部 Tag' : '翻译全部译文'} open={translationScope !== null}
            onCancel={() => { if (!startingTranslation) setTranslationScope(null); }} onOk={translateAll}
            confirmLoading={startingTranslation} okText="确认覆盖并翻译" cancelText="取消" maskClosable={false} closable={!startingTranslation} keyboard={!startingTranslation}
            okButtonProps={{ danger: true, disabled: translationJob?.status === 'running' }} cancelButtonProps={{ disabled: startingTranslation }}>
            <Alert type="warning" showIcon message="此操作会覆盖现有译文" style={{ marginBottom: 16 }} />
            <Typography.Paragraph>将重新翻译{translationScope === 'tag' ? '全部 Tag' : '所有分类下的全部问题'}，不受当前分类、搜索或分页限制。</Typography.Paragraph>
            <Typography.Paragraph><Typography.Text strong>简体中文、繁体中文、日语、泰语、阿拉伯语</Typography.Text>的现有记录将被覆盖，<Typography.Text strong>包括管理员手动修改的译文</Typography.Text>。</Typography.Paragraph>
            <Typography.Paragraph>{translationScope === 'tag' ? '问题译文' : 'Tag 译文'}及英文原文保持不变。翻译失败的条目会保留原译文。</Typography.Paragraph>
        </Modal></PermissionControl>
        <RecommendationTranslations target={translationTarget} onClose={() => setTranslationTarget(null)} onSaved={refresh} />
    </>;
}

export default function AIRecommendationsScreen() {
    return <div className="main-wrapper"><div className="cnt-item" style={{ padding: 24 }}>
        <Typography.Title level={4}>深度研究推荐</Typography.Title>
        <RecommendationPanel />
    </div></div>;
}
